import { useTranslation } from 'react-i18next'

import { ConfirmDialog } from '~/components/common/ConfirmDialog'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { useDeleteGenerationSalesMaster } from '~/pages/AdminOcctoMaster/tabs/GenerationSalesPlanMaster/hooks/useDeleteGenerationSalesMaster'
import type { GenerationSalesMasterRow } from '~/pages/AdminOcctoMaster/tabs/GenerationSalesPlanMaster/types'
import { mapMasterDeleteError } from '~/pages/AdminOcctoMaster/tabs/_common/deleteError'

type Props = {
  isOpen: boolean
  row: GenerationSalesMasterRow
  onClose: () => void
  onError: (message: string) => void
  errorMessage: string | undefined
}

/**
 * 発電販売計画マスタ 削除確認ダイアログ。
 * optimistic lock エラー時は onError コールバックで親にエラーを通知する。
 */
export const DeleteGenerationSalesPlanMasterDialog = ({
  isOpen,
  row,
  onClose,
  onError,
  errorMessage,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const showSuccessToast = useSuccessToast()
  const { mutateAsync, isPending } = useDeleteGenerationSalesMaster()

  const handleConfirm = async (): Promise<void> => {
    try {
      await mutateAsync({
        id: row.id,
        // row.updatedAt は list response の proto Timestamp をそのまま round-trip。
        // JS Date を経由すると µs 精度が落ち、backend の DB updated_at (= µs 精度)
        // との optimistic-lock 比較が一致せず Aborted で返る。
        expectedUpdatedAt: row.updatedAt,
      })
      onClose()
      // 削除成功の toast (2026-08-28 デザイナー指摘)。
      showSuccessToast({ title: t('common:toast.deleted') })
    } catch (err) {
      onError(
        mapMasterDeleteError(
          t,
          err,
          'page:adminOcctoMaster.generationSalesPlanMaster.serverError',
        ),
      )
    }
  }

  return (
    <ConfirmDialog
      body={
        errorMessage ??
        t(
          'page:adminOcctoMaster.generationSalesPlanMaster.delete.confirmBody',
          {
            submitterCode: row.submitterCode,
          },
        )
      }
      confirmLabel={t(
        'page:adminOcctoMaster.generationSalesPlanMaster.delete.confirmLabel',
      )}
      destructive
      isLoading={isPending}
      isOpen={isOpen}
      onCancel={onClose}
      onConfirm={() => {
        void handleConfirm()
      }}
      title={t(
        'page:adminOcctoMaster.generationSalesPlanMaster.delete.confirmTitle',
      )}
    />
  )
}
